import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/formularios")({
  head: () => ({
    meta: [
      { title: "Admin — Formularios | Mine Class" },
      {
        name: "description",
        content:
          "Seguimiento de formularios iniciados, completados y tasa de abandono — acceso restringido a Mine Class.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: FormEventsAdmin,
});

type FormEvent = {
  id: string;
  session_id: string;
  form_id: string;
  status: "iniciado" | "completado";
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  page_path: string | null;
  created_at: string;
  updated_at: string;
};

function isoDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function pct(n: number) {
  return `${n.toFixed(1)}%`;
}

function Summary({
  rows,
  groupBy,
  label,
}: {
  rows: FormEvent[];
  groupBy: (r: FormEvent) => string;
  label: string;
}) {
  const summary = useMemo(() => {
    const map = new Map<string, { iniciados: number; completados: number }>();
    for (const r of rows) {
      const key = groupBy(r) || "(sin dato)";
      const cur = map.get(key) ?? { iniciados: 0, completados: 0 };
      cur.iniciados += 1;
      if (r.status === "completado") cur.completados += 1;
      map.set(key, cur);
    }
    return [...map.entries()]
      .map(([key, v]) => ({
        key,
        ...v,
        abandono: v.iniciados ? ((v.iniciados - v.completados) / v.iniciados) * 100 : 0,
      }))
      .sort((a, b) => b.iniciados - a.iniciados);
  }, [rows, groupBy]);

  return (
    <div className="overflow-x-auto border border-[var(--color-outline-variant)] rounded-lg">
      <table className="w-full text-sm">
        <caption className="sr-only">{label}</caption>
        <thead className="bg-[var(--color-surface-container-low)] text-left">
          <tr>
            <th scope="col" className="px-3 py-2 font-semibold">{label}</th>
            <th scope="col" className="px-3 py-2 font-semibold">Iniciados</th>
            <th scope="col" className="px-3 py-2 font-semibold">Completados</th>
            <th scope="col" className="px-3 py-2 font-semibold">Tasa de abandono</th>
          </tr>
        </thead>
        <tbody>
          {summary.length === 0 && (
            <tr>
              <td colSpan={4} className="px-3 py-4 text-[var(--color-on-surface-variant)]">
                Sin datos en el rango seleccionado.
              </td>
            </tr>
          )}
          {summary.map((s) => (
            <tr key={s.key} className="border-t border-[var(--color-outline-variant)]">
              <td className="px-3 py-2">{s.key}</td>
              <td className="px-3 py-2">{s.iniciados}</td>
              <td className="px-3 py-2">{s.completados}</td>
              <td className="px-3 py-2 font-semibold">{pct(s.abandono)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FormEventsAdmin() {
  const [rows, setRows] = useState<FormEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [formFilter, setFormFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      const { data, error } = await supabase
        .from("form_events")
        .select("*")
        .gte("created_at", `${from}T00:00:00Z`)
        .lte("created_at", `${to}T23:59:59Z`)
        .order("created_at", { ascending: false })
        .limit(2000);
      if (cancelled) return;
      if (error) setErr(error.message);
      else setRows((data ?? []) as FormEvent[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const formIds = useMemo(
    () => [...new Set(rows.map((r) => r.form_id))].sort(),
    [rows],
  );

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!formFilter || r.form_id === formFilter) &&
          (!statusFilter || r.status === statusFilter),
      ),
    [rows, formFilter, statusFilter],
  );

  const totals = useMemo(() => {
    const iniciados = filtered.length;
    const completados = filtered.filter((r) => r.status === "completado").length;
    return {
      iniciados,
      completados,
      abandono: iniciados ? ((iniciados - completados) / iniciados) * 100 : 0,
    };
  }, [filtered]);

  const inputCls =
    "border border-[var(--color-outline-variant)] bg-transparent px-3 py-2 rounded min-h-[44px]";

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-[var(--color-brand-purple)]">
          Formularios · iniciados vs. completados
        </h1>
        <p className="text-sm text-[var(--color-on-surface-variant)] mt-1">
          Tasa de abandono = (iniciados − completados) / iniciados × 100
        </p>
      </header>

      <section aria-label="Filtros" className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label htmlFor="fe-from" className="text-xs font-semibold uppercase tracking-wide">Desde</label>
          <input id="fe-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="fe-to" className="text-xs font-semibold uppercase tracking-wide">Hasta</label>
          <input id="fe-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="fe-form" className="text-xs font-semibold uppercase tracking-wide">Formulario</label>
          <select id="fe-form" value={formFilter} onChange={(e) => setFormFilter(e.target.value)} className={inputCls}>
            <option value="">Todos</option>
            {formIds.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="fe-status" className="text-xs font-semibold uppercase tracking-wide">Estado</label>
          <select id="fe-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls}>
            <option value="">Todos</option>
            <option value="iniciado">Iniciado</option>
            <option value="completado">Completado</option>
          </select>
        </div>
      </section>

      {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
      {loading && <p className="text-sm text-[var(--color-on-surface-variant)]">Cargando…</p>}

      <section aria-label="Totales" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { l: "Iniciados", v: String(totals.iniciados) },
          { l: "Completados", v: String(totals.completados) },
          { l: "Tasa de abandono", v: pct(totals.abandono) },
        ].map((c) => (
          <div key={c.l} className="border border-[var(--color-outline-variant)] rounded-lg p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--color-on-surface-variant)]">{c.l}</p>
            <p className="text-2xl font-bold mt-1">{c.v}</p>
          </div>
        ))}
      </section>

      <section aria-label="Resumen por formulario" className="space-y-3">
        <h2 className="text-lg font-bold">Resumen por formulario</h2>
        <Summary rows={filtered} groupBy={(r) => r.form_id} label="Formulario" />
      </section>

      <section aria-label="Resumen por canal" className="space-y-3">
        <h2 className="text-lg font-bold">Resumen por utm_source</h2>
        <Summary rows={filtered} groupBy={(r) => r.utm_source ?? ""} label="utm_source" />
      </section>

      <section aria-label="Resumen por campaña" className="space-y-3">
        <h2 className="text-lg font-bold">Resumen por utm_campaign</h2>
        <Summary rows={filtered} groupBy={(r) => r.utm_campaign ?? ""} label="utm_campaign" />
      </section>

      <section aria-label="Registros" className="space-y-3">
        <h2 className="text-lg font-bold">Registros ({filtered.length})</h2>
        <div className="overflow-x-auto border border-[var(--color-outline-variant)] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface-container-low)] text-left">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold">Formulario</th>
                <th scope="col" className="px-3 py-2 font-semibold">Estado</th>
                <th scope="col" className="px-3 py-2 font-semibold">Sesión</th>
                <th scope="col" className="px-3 py-2 font-semibold">Origen</th>
                <th scope="col" className="px-3 py-2 font-semibold">Campaña</th>
                <th scope="col" className="px-3 py-2 font-semibold">Página</th>
                <th scope="col" className="px-3 py-2 font-semibold">Inicio</th>
                <th scope="col" className="px-3 py-2 font-semibold">Actualizado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-[var(--color-outline-variant)]">
                  <td className="px-3 py-2">{r.form_id}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        r.status === "completado"
                          ? "px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800"
                          : "px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800"
                      }
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.session_id.slice(0, 8)}…</td>
                  <td className="px-3 py-2">{r.utm_source ?? "—"}</td>
                  <td className="px-3 py-2">{r.utm_campaign ?? "—"}</td>
                  <td className="px-3 py-2">{r.page_path ?? "—"}</td>
                  <td className="px-3 py-2">{new Date(r.created_at).toLocaleString("es-CL")}</td>
                  <td className="px-3 py-2">{new Date(r.updated_at).toLocaleString("es-CL")}</td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-[var(--color-on-surface-variant)]">
                    Sin registros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
